<template>
  <div class="space-y-6">
    <section class="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 mt-1 flex items-center gap-2">
            Async Session Overview
            <BaseInfoIcon :text="ERKLAERUNG.feedbackAsyncTab" label="Async Session Overview" />
          </h2>
          <p class="text-sm text-gray-500">Browse your session feedback, message ratings, and aggregated scores.</p>
        </div>
      </div>

      <div class="mt-4">
        <div v-if="loading" class="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-6 text-center text-indigo-700">
          Loading feedback…
        </div>

        <div
          v-else-if="error"
          class="rounded-xl border border-red-100 bg-red-50 px-4 py-4 text-red-700 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        >
          <span>{{ error }}</span>
          <BaseButton variant="delete" class="md:w-auto" @click="fetchSessionsList">
            Retry
          </BaseButton>
        </div>
      </div>

      <div
        v-if="!loading && !error && sessions.length"
        class="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4"
      >
        <div class="md:w-72">
          <label class="block text-sm font-semibold text-gray-700 mb-1" for="async-session-select">
            Async session
          </label>
          <BaseSelect
            id="async-session-select"
            v-model="selectedSessionId"
            :options="sessionOptions"
            showPlaceholder
            placeholder="Select a session"
          />
        </div>
      </div>

      <div
        v-else-if="!loading && !error"
        class="mt-6 rounded-xl border border-gray-100 bg-gray-50 px-4 py-5 text-sm text-gray-600"
      >
        No async sessions available.
      </div>
    </section>

    <template v-if="!loading && !error && feedback && selectedSession">
      <!-- Async Sessions Details -->
      <section class="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-xl font-semibold text-gray-900">Session details</h3>
            <p class="text-sm text-gray-500">Insights for the selected async session.</p>
          </div>
        </div>
        <div class="rounded-2xl border border-gray-100">
          <article class="p-5">
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h4 class="text-lg font-semibold text-gray-900">{{ selectedSession.title }}</h4>
                <p class="text-sm text-gray-500" v-if="selectedSession.description">{{ selectedSession.description }}</p>
              </div>
              <div class="text-sm text-gray-500 space-y-1 md:text-right">
                <p v-if="selectedSession.start_date"><strong class="font-semibold">Start:</strong> {{ formatDate(selectedSession.start_date) }}</p>
                <p v-if="selectedSession.end_date"><strong class="font-semibold">End:</strong> {{ formatDate(selectedSession.end_date) }}</p>
              </div>
            </div>

            <div v-if="selectedSession.categories && selectedSession.categories?.length" class="mb-4 flex flex-wrap gap-2">
              <span
                v-for="category in selectedSession.categories"
                :key="category"
                class="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700"
              >
                {{ category }}
              </span>
            </div>

            <div v-if="selectedSession.user_session" class="grid gap-4 md:grid-cols-2">
              <div class="bg-indigo-50 rounded-lg p-4">
                <p class="text-xs uppercase font-semibold text-indigo-700 tracking-wide mb-1">Grade</p>
                <p class="text-2xl font-bold text-indigo-900">
                  <span v-if="selectedSession.user_session.grade_released">{{ selectedSession.user_session.grade ?? "—" }}</span>
                  <span v-else class="text-gray-400 italic text-base">Not released yet</span>
                </p>
              </div>
              <div class="bg-indigo-50 rounded-lg p-4">
                <p class="text-xs uppercase font-semibold text-indigo-700 tracking-wide mb-1">Goal</p>
                <p class="text-sm text-gray-800">{{ selectedSession.user_session.goal || "No goal provided yet." }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4 md:col-span-2">
                <p class="text-xs uppercase font-semibold text-gray-500 tracking-wide mb-1">Evaluation</p>
                <p class="text-sm text-gray-800 whitespace-pre-line">
                  <span v-if="selectedSession.user_session.evaluation_released">{{ selectedSession.user_session.evaluation || "—" }}</span>
                  <span v-else class="text-gray-400 italic">Not released yet</span>
                </p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4 md:col-span-2">
                <p class="text-xs uppercase font-semibold text-gray-500 tracking-wide mb-1">Recommendation</p>
                <p class="text-sm text-gray-800 whitespace-pre-line">
                  <span v-if="selectedSession.user_session.recommendation_released">{{ selectedSession.user_session.recommendation || "—" }}</span>
                  <span v-else class="text-gray-400 italic">Not released yet</span>
                </p>
              </div>
            </div>
            <p v-else class="text-sm text-gray-500">No feedback recorded for this session yet.</p>

            <div v-if="selectedSession.comments?.length" class="mt-4 border-t pt-4">
              <p class="text-sm font-semibold text-gray-700 mb-2">Comments</p>
              <ul class="space-y-2">
                <li
                  v-for="comment in selectedSession.comments"
                  :key="comment.id"
                  class="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2"
                >
                  <p class="font-medium text-gray-900">{{ comment.text }}</p>
                  <p class="text-xs text-gray-500">Created at {{ formatDate(comment.created_at) }}</p>
                </li>
              </ul>
            </div>
            <p v-else class="text-sm text-gray-500 mt-4">No comments for this session yet.</p>
          </article>
        </div>
      </section>
      <!-- Average Criteria -->
      <section class="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-xl font-semibold text-gray-900">
              Average ratings for {{ selectedSession.title }}
            </h3>
            <p class="text-sm text-gray-500">
              Scores based on the reviewed messages linked to this async session.
            </p>
          </div>
        </div>
        <div v-if="mergedCriteriaAverages.length" class="overflow-x-auto rounded-xl border border-gray-100">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Criterion</th>
                <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Your Average</th>
                <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Your Ratings</th>
                <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Overall Average</th>
                <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Ratings</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-100">
              <tr v-for="criterion in mergedCriteriaAverages" :key="criterion.criterion_id">
                <td class="px-4 py-3 text-sm text-gray-800">{{ criterion.criterion_name }}</td>
                <td class="px-4 py-3 text-sm font-semibold text-gray-900">
                  {{ formatAverage(criterion.user_average) || '—' }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-700">{{ criterion.user_ratings || 0 }}</td>
                <td class="px-4 py-3 text-sm font-semibold text-gray-900">
                  {{ formatAverage(criterion.overall_average) || '—' }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-700">{{ criterion.overall_ratings || 0 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-sm text-gray-500">No rated criteria for this session yet.</p>
      </section>

      <!-- Messages -->
      <section class="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-xl font-semibold text-gray-900">Reviewed messages</h3>
            <p class="text-sm text-gray-500">Filtered to the selected async session.</p>
          </div>
        </div>
        <div v-if="selectedMessages.length" class="space-y-4">
          <article
            v-for="message in selectedMessages"
            :key="message.id"
            class="border border-gray-100 rounded-2xl p-5"
          >
            <div class="flex flex-col md:flex-row md:justify-between gap-4">
              <div>
                <p class="text-gray-900 whitespace-pre-line">{{ message.content }}</p>
                <a
                  v-if="message.link"
                  :href="message.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-indigo-600 hover:underline mt-1 inline-block"
                >
                  Open message
                </a>
              </div>
              <div class="text-sm text-gray-500 md:text-right space-y-1">
                <p><strong>Platform:</strong> {{ message.platform || "—" }}</p>
                <p><strong>Category:</strong> {{ message.category || "—" }}</p>
                <p><strong>Sent:</strong> {{ formatDate(message.sent_at || message.created_at) }}</p>
              </div>
            </div>

            <div v-if="message.criteria?.length" class="mt-4 overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Criterion</th>
                    <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Value</th>
                    <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Comment</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr v-for="criterion in message.criteria" :key="criterion.id">
                    <td class="px-4 py-3 text-sm text-gray-800">{{ criterion.criterion_name || "–" }}</td>
                    <td class="px-4 py-3 text-sm font-semibold text-gray-900">
                      {{ formatCriterionValue(criterion) }}
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-700">{{ criterion.comment || "—" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="text-sm text-gray-500 mt-3">No relevant criteria rated for this message yet.</p>
          </article>
        </div>
        <p v-else class="text-sm text-gray-500">There are no reviewed messages for this session yet.</p>
      </section>
    </template>
  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import BaseSelect from "@/BaseComponents/BaseSelect.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";
import { getAsyncStudentFeedback } from "@/api/studentFeedback";
import { getStudentAsyncSessions } from "@/live-sessions/api/studentFeedback";

export default {
  name: "AsyncSessionsFeedback",
  components: { BaseButton, BaseInfoIcon, BaseSelect },
  data() {
    return {
      ERKLAERUNG,
      loading: false,
      error: null,
      feedback: null,
      sessionsList: [],
      selectedSessionId: null,
    };
  },
  computed: {
    routeUserId() {
      return this.$route.params.id;
    },
    sessions() {
      return this.sessionsList ?? [];
    },
    sessionOptions() {
      return this.sessions
        .map((session) => ({
          value: String(session.session_id ?? session.id),
          label: session.title || `Session ${session.session_id}`,
        }));
    },
    selectedSession() {
      if (!this.selectedSessionId) return null;
      const session = this.sessions.find(
        s => String(s.session_id ?? s.id) === String(this.selectedSessionId)
      );
      if (!session) return null;

      return {
        ...session,
        user_session: session.user_session ?? {},
        comments: session.comments ?? [],
        categories: session.categories ?? [],
      };
    },
    selectedMessages() {
      if (!this.selectedSession) {
        return [];
      }
      const targetId = String(this.selectedSession.session_id ?? this.selectedSession.id);
      const sessionCategories = Array.isArray(this.selectedSession.categories)
        ? this.selectedSession.categories
        : [];
      const messages = this.feedback?.messages ?? [];
      return messages.filter((message) => {
        if (message.session_id !== null && message.session_id !== undefined) {
          return String(message.session_id) === targetId;
        }
        if (sessionCategories.length && message.category) {
          return sessionCategories.includes(message.category);
        }
        return false;
      });
    },
    mergedCriteriaAverages() {
      // Get the feedback data from the API response
      const userAverages = this.feedback?.message_criteria_average ?? [];
      const allUsersAverages = this.feedback?.message_criteria_average_all_users ?? [];
      
      // Create a map of all criteria with their combined averages
      const criteriaMap = new Map();
      
      // Add user averages
      userAverages.forEach(criterion => {
        criteriaMap.set(criterion.criterion_id, {
          criterion_id: criterion.criterion_id,
          criterion_name: criterion.criterion_name,
          user_average: criterion.average_score,
          user_ratings: criterion.ratings_count,
          overall_average: null,
          overall_ratings: 0
        });
      });
      
      // Add/merge all users averages
      allUsersAverages.forEach(criterion => {
        const existing = criteriaMap.get(criterion.criterion_id) || {
          criterion_id: criterion.criterion_id,
          criterion_name: criterion.criterion_name,
          user_average: null,
          user_ratings: 0
        };
        
        existing.overall_average = criterion.average_score;
        existing.overall_ratings = criterion.ratings_count;
        
        criteriaMap.set(criterion.criterion_id, existing);
      });
      
      // Convert map to array and sort by criterion name
      return Array.from(criteriaMap.values())
        .sort((a, b) => a.criterion_name.localeCompare(b.criterion_name));
    },
  },
  watch: {
    routeUserId: {
      immediate: true,
      handler() {
        this.fetchSessionsList();
      },
    },
    selectedSessionId(newVal) {
      // Guard against the string 'undefined' coming from some selects and coerce to number
      if (!newVal || String(newVal) === "undefined") {
        this.feedback = null;
        return;
      }

      const sessionId = Number(newVal);
      if (!Number.isFinite(sessionId) || sessionId <= 0) {
        // invalid id selected; clear state and do nothing
        this.feedback = null;
        return;
      }

      this.checkAndLoadSession(sessionId);
    },
  },
  methods: {
    async fetchSessionsList() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await getStudentAsyncSessions();
        this.sessionsList = data;
        this.prepareSelectedSession();
      } catch (err) {
        this.error = err?.response?.data?.detail || "Unable to load sessions.";
        this.sessionsList = [];
        this.selectedSessionId = null;
      } finally {
        this.loading = false;
      }
    },

    async checkAndLoadSession(sessionId) {
      this.feedback = null; // clear previous detailed feedback

      try {
        // fetch the full async feedback (messages, averages, sessions with user_session)
        const { data: full } = await getAsyncStudentFeedback(this.routeUserId, sessionId);
        this.feedback = full;

        if (full.sessions && full.sessions.length) {
          const detailedSession = full.sessions[0];
          this.sessionsList = this.sessionsList.map(s => {
            const idMatch = String(s.session_id ?? s.id) === String(sessionId);
            if (idMatch) {
              return {
                ...s,
                ...detailedSession,
                user_session: detailedSession.user_session ?? {},
                comments: detailedSession.comments ?? [],
                categories: detailedSession.categories ?? [],
              };
            }
            return s;
          });

          // ensure selectedSessionId points to the merged session
          this.selectedSessionId = String(sessionId);
        }
      } catch (err) {
        console.error("Failed to load session:", err);
        this.error = err?.response?.data?.detail || "Failed to load session details.";
        this.feedback = null;
      }
    },
    prepareSelectedSession() {
      if (!this.sessions.length) {
        this.selectedSessionId = null;
        return;
      }
      // default to first session if none selected
      const firstSession = this.sessions[0];
      if (firstSession) this.selectedSessionId = String(firstSession.session_id ?? firstSession.id);
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },
    formatAverage(value) {
      if (value === null || value === undefined) return "—";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return "—";
      }
      return numeric.toFixed(2);
    },
    formatCriterionValue(criterion) {
      if (criterion.count_value !== null && criterion.count_value !== undefined) {
        const val = Number(criterion.count_value);
        return val > 0 ? `+${val}` : `${val}`;
      }
      if (criterion.is_fulfilled !== null && criterion.is_fulfilled !== undefined) {
        return criterion.is_fulfilled ? "Met" : "Not met";
      }
      if (criterion.text_value) {
        return criterion.text_value;
      }
      return "—";
    },
  },
};
</script>
